import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// GET /users - Get all users
export const getUsers = createAsyncThunk("users/getUsers", async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      return rejectWithValue("Session not found.");
    }

    const response = await axios.get(`${API_URL}/users`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error.response?.data?.message || "Users could not be retrieved."
    );
  }
});

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async (userData) => {
    try {
      const token = localStorage.getItem("token");
      
      // userData bir FormData mı yoksa doğrudan obje mi kontrol et
      const isFormData = userData instanceof FormData;
      
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      
      // Eğer FormData değilse, Content-Type header'ını ekle
      if (!isFormData) {
        headers["Content-Type"] = "application/json";
      }

      const response = await axios.put(`${API_URL}/users`, userData, {
        headers: headers
      });
      
      console.log("Backend'den gelen cevap:", JSON.stringify(response.data, null, 2));
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "User details could not be updated."
      );
    }
  }
);

// GET /users/current-user - Get user data for the current user
export const getCurrentUser = createAsyncThunk(
  "users/getCurrentUser",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        return rejectWithValue("Session not found.");
      }
      
      const response = await axios.get(`${API_URL}/users/current-user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const userData = response.data.user || response.data;
      return userData;
    } catch (error) {
      if (error.response?.status === 404) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
      
      return rejectWithValue(
        error.response?.data?.message || "Current user data could not be retrieved."
      );
    }
  }
);

// GET /users/{id} - Get user by ID
export const getUserById = createAsyncThunk("users/getUserById", async (id) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/users/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "User could not be retrieved by ID."
    );
  }
});

// DELETE /users/{id} - Delete user
export const deleteUser = createAsyncThunk("users/deleteUser", async (id) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.delete(`${API_URL}/users/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "User could not be deleted.");
  }
});

// PUT /users/password/change - Change user password with old password verification
export const changePassword = createAsyncThunk(
  "users/changePassword",
  async (passwordData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/users/password/change`,
        passwordData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Password could not be changed."
      );
    }
  }
);

// POST /users/forgot-password - Send password reset email
export const forgotPassword = createAsyncThunk(
  "users/forgotPassword",
  async (emailData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/users/forgot-password`,
        emailData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Şifre sıfırlama e-postası gönderilemedi."
      );
    }
  }
);

// POST /users/set-new-password/:id/:token - Set new password with token
export const setNewPassword = createAsyncThunk(
  "users/setNewPassword",
  async ({ id, token, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/users/set-new-password/${id}/${token}`,
        { password },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Şifre sıfırlanamadı."
      );
    }
  }
);

// POST /upload/image - Upload user image
export const uploadUserImage = createAsyncThunk(
  "users/uploadUserImage",
  async (file) => {
    try {
      const formData = new FormData();
      formData.append("picture", file);

      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Photo could not be uploaded."
      );
    }
  }
);

// Slice oluşturma
const usersSlice = createSlice({
  name: "users",
  initialState: {
    users: [],
    currentUser: null,
    selectedUser: null,
    loading: false,
    error: null,
    forgotPasswordMessage: null,
    resetPasswordMessage: null,
  },
  reducers: {
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearForgotPasswordMessage: (state) => {
      state.forgotPasswordMessage = null;
    },
    clearResetPasswordMessage: (state) => {
      state.resetPasswordMessage = null;
    },
  },
  extraReducers: (builder) => {
    // fetchUsers
    builder
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users || action.payload;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // updateUser
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload.user || action.payload;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // fetchCurrentUser
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload; 
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // fetchUserById
      .addCase(getUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload.user || action.payload;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // deleteUser
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // changePassword
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // forgotPassword
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.forgotPasswordMessage = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.forgotPasswordMessage = action.payload.message;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // setNewPassword
      .addCase(setNewPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.resetPasswordMessage = null;
      })
      .addCase(setNewPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.resetPasswordMessage = action.payload.message;
      })
      .addCase(setNewPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // uploadUserImage
      .addCase(uploadUserImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadUserImage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadUserImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

// Export slice ve action'lar
export default usersSlice.reducer;
export const { 
  setSelectedUser, 
  clearSelectedUser, 
  clearError, 
  clearForgotPasswordMessage,
  clearResetPasswordMessage
} = usersSlice.actions;